/* eslint-disable camelcase */
import logger, { chalk, text } from '@clamjs/logger';
import { execaCommandSync } from 'execa';
import got from 'got';
import shelljs from 'shelljs';

import { getUnknowErrorMsg, runShell } from './tools';

const TOKEN = 'rXn7iEvs1JYtKkE44fzj';
const CODE_HOST = 'https://code.alibaba-inc.com';

const debug = logger.debug('gitlab');
const GIT_ACCOUNT_NAME = 'clam-bot';

export interface CreateProject {
  id: number;
  ssh_url_to_repo: string;
  http_url_to_repo: string;
}

export const getProject = async (path: string) => {
  const target = encodeURIComponent(path);

  return await got
    .get(`${CODE_HOST}/api/v3/projects/search/${target}?private_token=${TOKEN}`)
    .json<{ name: string }[]>();
};

export const getGroup = async (group: string) => {
  return await got
    .get(`${CODE_HOST}/api/v3/group/find_by_path?path=${group}&private_token=${TOKEN}`)
    .json<{ id: number }>();
};

export const createProject = async (params: { id: number; name: string; description: string }) => {
  try {
    const response = await got
      .post(`${CODE_HOST}/api/v3/projects?private_token=${TOKEN}`, {
        json: {
          name: params.name,
          namespace_id: params.id,
          description: params.description,
          issues_enabled: true,
          merge_requests_enabled: true,
          wiki_enabled: true,
          snippets_enabled: false,
          visibility_level: 10,
        },
      })
      .json<CreateProject>();

    const { id, ssh_url_to_repo, http_url_to_repo } = response;

    return {
      id,
      sshRepo: ssh_url_to_repo,
      httpRepo: http_url_to_repo,
    };
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

/**
 * 获取项目当前 git 分支
 *
 * @param noPrefix TODO 去掉 noPrefix. 目前分支命名格式是 daily/x.y.z，noPrefix=true 即为获取 x.y.z;
 *   新的版本管理，要和分支名称解耦，届时不再需要 noPrefix
 */
export const getCurrentBranch = (noPrefix = false) => {
  const branch = runShell('git rev-parse --abbrev-ref HEAD');
  return noPrefix ? branch.replace(/.*\//u, '') : branch;
};

export const getCommitId = () => {
  return runShell('git rev-parse HEAD');
};

export const needCommit = () => {
  return runShell('git status --porcelain').length > 0;
};

export const needPush = () => {
  let ret;
  const currentBranch = getCurrentBranch();

  /**
   * 字面量传参报错: 没有与此调用匹配的重载。最后一个重载给出了错误：对象字面量只能指定已知属性，并且‘silent’不在类型“ExecCallback”中。（ts2769）
   * 这里用变量传参写法，ts 就认识了
   *
   *     const { code, stdout, stderr } = shelljs.exec(`git cherry -v origin/${currentBranch}`, {
   *       silent: true,
   *       pwd: process.cwd(),
   *     });
   */
  const options = { silent: true, pwd: process.cwd() };
  const { code, stdout, stderr } = shelljs.exec(`git cherry -v origin/${currentBranch}`, options);
  ret = code !== 0 ? stderr : stdout;
  ret = ret.trim();
  return ret.indexOf('fatal:') > -1 || (ret.length > 0 && ret.indexOf('fatal:') < 0);
};

export const getOriginMainBranch = () => {
  // 读 remote 的默认主分支（默认主分支可以在 code 上修改，本地 git config defaultBranch 可能不是最新）
  // HEAD branch|HEAD 分支 中英文兼容
  const shell = `git remote show origin | grep -E 'HEAD branch|HEAD 分支' | awk -F'[:：]' '{print $2}'`;
  const branch = runShell(shell);
  return ['unknown', ''].includes(branch) ? 'master' : branch;
};

const branchPrefixs = ['feat/', 'fix/', 'chore/'];
const getNewBranchName = async () => {
  const prefixTip = branchPrefixs.map((prefix) => `"${prefix}"`).join('、');
  const answer = await text({
    message: `输入分支名称, 以 ${prefixTip} 开头`,
    validate: (name: string) => {
      const isNameValid = branchPrefixs.some((prefix) => name.startsWith(prefix));
      if (!isNameValid) {
        return `分支名称必须以 ${prefixTip} 开头`;
      }
      return undefined;
    },
  });

  if (logger.isCancel(answer)) {
    logger.cancel('用户取消操作');
    process.exit(0);
  }
  debug(`getNewBranchName() input branch name: ${answer}`);
  return answer;
};

/**
 * 新建分支
 *
 * @branch 新分支名称
 * @baseBranch 新分支基于的分支
 */
export const createBranch = async (branch?: string, baseBranch?: string) => {
  // 1. 检测分支是否干净
  if (needCommit()) {
    logger.fatal('存在未 commit 的代码，请先提交（可以使用 "git status" 查看状态）');
  }
  // 2. 提问获取新分支的名称
  const branchName = branch || (await getNewBranchName());
  // 3. 基于主干创建新分支。同步远端主干，基于远端主干新建分支并切换到该分支
  const mainBranch = baseBranch || getOriginMainBranch();
  logger.info(`创建新分支 ${branchName}, 基于主干 ${chalk.bgYellow(mainBranch)}`);
  runShell(`git fetch origin && git checkout -b ${branchName} origin/${mainBranch}`);
  const currentBranch = getCurrentBranch();
  debug(`createBranch() success, new branch: ${currentBranch}`);
  return currentBranch;
};

const CLAM_BOT_ACCOUNT = {
  name: 'clambot',
  email: 'clambot@fliggy.com',
};
export const CLAM_BOT_INFO = {
  ...CLAM_BOT_ACCOUNT,
  envVariable: `GIT_AUTHOR_NAME=${CLAM_BOT_ACCOUNT.name} GIT_AUTHOR_EMAIL=${CLAM_BOT_ACCOUNT.email}`,
};

export const commit = (message: string) => {
  runShell(`git add . -A && ${CLAM_BOT_INFO.envVariable} git commit -m "${message}"`);
};

export const push = (commitMessage?: string) => {
  if (needCommit()) {
    commit(`${commitMessage || 'feat: commit'} by ${GIT_ACCOUNT_NAME}`);
  }
  if (needPush()) {
    runShell('git push -u origin HEAD');
  }
};

export const getLatestGitTags = (prefix = 'publish/', merged = true) => {
  const mainBranch = getOriginMainBranch();
  const result = runShell(
    `git fetch --tags && git tag ${prefix ? `--list "${prefix}*"` : ''} ${
      merged ? `--merged=${mainBranch}` : ''
    } --sort=-taggerdate`,
  );
  const tags = result?.length ? result.split('\n') : [];
  debug('getGitTags: ', tags);
  return tags;
};

export const getGitOriginUrl = () => {
  try {
    const gitUrl = runShell('git remote get-url origin');
    return gitUrl;
  } catch (error) {
    const msg = getUnknowErrorMsg(error);
    throw new Error(`获取 git origin url 失败，${msg}`);
  }
};

/** 回到主干分支 */
export const checkoutMainBranch = () => {
  const checkoutCmd = `git checkout ${getOriginMainBranch()}`;
  debug(`执行命令：${checkoutCmd}`);
  const res = execaCommandSync(checkoutCmd, {
    stdio: 'inherit',
  });
  if (!res.failed) {
    const pullCmd = 'git pull';
    debug(`执行命令：${pullCmd}`);
    execaCommandSync(pullCmd, {
      stdio: 'inherit',
    });
  }
};
